import { getHasDeposit } from '../../_ui-core_/base/package/api/package/pam/pam-api';
import type { Balance } from '../../_ui-core_/base/package/api/package/types';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { localStorage_getOrNull } from '../../_ui-core_/base/package/util/localStorage';
import { formatMoney } from '../../_ui-core_/base/package/util/money';
import { UIFlyerBar } from '../../_ui-core_/components/ui-flyer-bar/ui-flyer-bar';
import type { CasinoConfig } from '../../app/package/base/cms/package/cms-domain';
import { LoginStatus } from '../../app/package/base/service/login/login-domain';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import { LOCAL_STORAGE_KEY_HAS_DEPOSIT_OVERRIDE, REPORT_4XX__RETRY_REPORT_500 } from '../../constants';

export class LowBalanceFlyerController {
  private readonly _subHelper = new SubHelper();
  private _destroyed = false;
  private _hasDeposited = false;
  private _balance: number | null = null;
  private _flyer: UIFlyerBar | null = null;
  private readonly _config?: { enabled: boolean; thresholdCents: number };

  constructor(config: CasinoConfig['lowBalanceFlyer']) {
    this._config = config;
  }

  /** Async entry point — kept out of the constructor (sonarqube:typescript:S7059). */
  init(): void {
    // init() is called from an idle-gated callback in base-page.ts, so the page
    // may have already disconnected (fast navigation) by the time this runs.
    if (this._destroyed) return;
    if (!this._config?.enabled) return;

    this._subHelper.addSub(window.$app.balanceStore, (bal) => this._onBalance(bal), true);
    this._subHelper.addSub(window.$app.routerStore, (route) => this._onRoute(route.url), false);

    // Deposit status is immutable for the session — resolve it once via the
    // authoritative final login status instead of reacting to every loginStore emission.
    window.$app.login.getFinalLoginStatus().then((status) => this._onLogin(status));
  }

  destroy() {
    this._destroyed = true;
    this._subHelper.unsubscribeAll();
  }

  private async _onLogin(status: LoginStatus) {
    if (this._destroyed) return;
    if (status === LoginStatus.LOGGED_IN) {
      this._hasDeposited = await this._checkHasDeposited();
      if (this._destroyed) return;
      this._evaluate();
    }
  }

  private _onBalance(balance: Balance) {
    this._balance = balance.amount === null ? null : balance.amount + balance.bonus;

    if (this._flyer && this._balance !== null) {
      if (this._balance >= this._config!.thresholdCents) {
        this._dismiss();
      } else {
        this._flyer.subtitle = `${window.$app.$t.get(I18n.flyer.lowBalance.subtitle)} ${formatMoney(this._balance)}`;
      }
    }

    this._evaluate();
  }

  private _onRoute(url: string) {
    const isHome = !url || url === '/';
    if (!isHome && this._flyer) {
      this._dismiss();
    } else if (isHome) {
      this._evaluate();
    }
  }

  private _evaluate() {
    if (!this._config?.enabled) return;
    if (this._flyer) return;
    if (!this._hasDeposited) return;
    if (this._balance === null) return;
    if (!this._isHomePage()) return;
    if (this._balance >= this._config.thresholdCents) return;

    const flyer = new UIFlyerBar();
    flyer.priority = 'promotional';
    flyer.title = window.$app.$t.get(I18n.flyer.lowBalance.title);
    flyer.subtitle = `${window.$app.$t.get(I18n.flyer.lowBalance.subtitle)} ${formatMoney(this._balance)}`;
    flyer.ctaLabel = window.$app.$t.get(I18n.base.deposit);
    flyer.onCtaClick = () => window.$app.product.gotoDeposit(false, TrackingEventSource.LOW_BALANCE_FLYER);
    this._flyer = flyer;
    window.$app.flyer.show(flyer, 'promotional');
  }

  private _dismiss() {
    window.$app.flyer.closeAll('promotional');
    this._flyer = null;
  }

  private _checkHasDeposited(): Promise<boolean> {
    const override = localStorage_getOrNull<boolean>(LOCAL_STORAGE_KEY_HAS_DEPOSIT_OVERRIDE);
    if (override !== null) return Promise.resolve(override);

    return window.$app.http
      .call(
        window.$app.appConfig.apiUrl_pam,
        getHasDeposit(window.$app.loginStore.value.jwt!),
        REPORT_4XX__RETRY_REPORT_500,
      )
      .catch(() => false);
  }

  private _isHomePage(): boolean {
    const url = window.$app.routerStore.value.url;
    return !url || url === '/';
  }
}
